import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, Row, Space, Switch, Tag, notification } from 'antd';
import { CreditCardOutlined, SaveOutlined } from '@ant-design/icons';
import stripeSettingsService from '../../services/stripeSettingsService';

const DEFAULT_ACCOUNTS = ['stripe_1', 'stripe_2', 'stripe_3'].map((accountType, index) => ({
  account_type: accountType,
  account_label: `Stripe ${index + 1}`,
  display_name: `Stripe ${index + 1}`,
  publishable_key: '',
  secret_key: '',
  has_secret_key: false,
  clear_secret: false,
  is_active: false,
}));

export default function StripeSettingsPage() {
  const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stripeSettingsService.getAll();
      const received = response.accounts || [];
      setAccounts(DEFAULT_ACCOUNTS.map(defaultAccount => {
        const account = received.find(item => item.account_type === defaultAccount.account_type);
        return {
          ...defaultAccount,
          ...account,
          secret_key: '',
          clear_secret: false,
        };
      }));
    } catch (error) {
      notification.error({
        message: 'Unable to load Stripe settings',
        description: error.message || 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateAccount = useCallback((accountType, field, value) => {
    setAccounts(current => current.map(account => (
      account.account_type === accountType ? { ...account, [field]: value } : account
    )));
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const response = await stripeSettingsService.updateAll(accounts.map(account => ({
        account_type: account.account_type,
        display_name: account.display_name,
        publishable_key: account.publishable_key || null,
        secret_key: account.secret_key || null,
        clear_secret: Boolean(account.clear_secret),
        is_active: Boolean(account.is_active),
      })));
      const received = response.accounts || [];
      setAccounts(current => current.map(currentAccount => {
        const saved = received.find(item => item.account_type === currentAccount.account_type);
        return {
          ...currentAccount,
          ...saved,
          secret_key: '',
          clear_secret: false,
        };
      }));
      notification.success({ message: 'Stripe accounts saved' });
    } catch (error) {
      notification.error({
        message: 'Stripe accounts were not saved',
        description: error.message || 'Check the account settings and try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-golden-navy mb-4">Stripe Settings</h1>
      <Alert
        className="mb-4"
        type="info"
        showIcon
        message="Stripe accounts are configured once here. Each user only selects which active Stripe account to use."
      />

      <Row gutter={[16, 16]}>
        {accounts.map(account => (
          <Col xs={24} xl={8} key={account.account_type}>
            <Card
              loading={loading}
              title={(
                <Space>
                  <CreditCardOutlined />
                  {account.account_label}
                  <Tag color={account.is_active ? 'green' : 'default'}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </Tag>
                </Space>
              )}
            >
              <Form layout="vertical">
                <Form.Item label="Account name" required>
                  <Input
                    value={account.display_name}
                    onChange={event => updateAccount(account.account_type, 'display_name', event.target.value)}
                    placeholder={account.account_label}
                  />
                </Form.Item>

                <Form.Item label="Publishable key">
                  <Input
                    value={account.publishable_key || ''}
                    onChange={event => updateAccount(account.account_type, 'publishable_key', event.target.value)}
                    placeholder="pk_live_..."
                  />
                </Form.Item>

                <Form.Item label="Secret key">
                  <Input.Password
                    value={account.secret_key}
                    onChange={event => updateAccount(account.account_type, 'secret_key', event.target.value)}
                    placeholder={account.has_secret_key ? 'Saved - leave blank to keep current' : 'sk_live_...'}
                  />
                </Form.Item>

                {account.has_secret_key ? (
                  <Form.Item label="Remove saved secret">
                    <Switch
                      checked={Boolean(account.clear_secret)}
                      onChange={checked => updateAccount(account.account_type, 'clear_secret', checked)}
                    />
                  </Form.Item>
                ) : null}

                <Form.Item label="Available for users">
                  <Switch
                    checked={Boolean(account.is_active)}
                    onChange={checked => updateAccount(account.account_type, 'is_active', checked)}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Form>
            </Card>
          </Col>
        ))}
      </Row>

      <Button
        className="bg-golden-navy mt-4"
        type="primary"
        icon={<SaveOutlined />}
        loading={saving}
        onClick={save}
      >
        Save Stripe Accounts
      </Button>
    </div>
  );
}
